import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ChevronDown, LogOut, User as UserIcon, Settings as SettingsIcon, ShieldCheck, ShieldOff, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { getUser, getUserInitials, getUserDisplayName, clearAuth, getToken } from '@/utils/auth';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Unified user account menu — replaces the standalone Logout button in the
 * desktop header. Built on Radix DropdownMenu which renders into a portal at
 * the root of the DOM, so it stays clickable even when full-viewport overlays
 * (WhatsNewSplash z-9999, modals, etc.) are mounted.
 *
 * Items:
 *   - "View profile"  → /profile
 *   - "Settings"      → /settings
 *   - (Admins only) Quick link to "System Health"
 *   - "Log out"       → clears auth + redirects to /login
 *
 * data-testid="user-menu-trigger" / "user-menu-logout" / "user-menu-profile"
 * etc. for testability.
 */
export default function UserAccountMenu() {
  const navigate = useNavigate();
  const user = getUser();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);
  if (!user) return null;

  const isAdmin = user.role === 'HQ' || user.role === 'ADMIN';
  const initials = getUserInitials();
  const displayName = getUserDisplayName();

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const handleSignOutEverywhere = async () => {
    setRevoking(true);
    try {
      const res = await fetch(`${API}/api/auth/logout-everywhere`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Signed out from all devices', {
        description: 'All other sessions have been revoked.',
      });
      clearAuth();
      navigate('/login');
    } catch (err) {
      toast.error('Could not sign out everywhere', { description: String(err.message || err) });
      setRevoking(false);
    }
  };

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger
        className="flex items-center gap-2 rounded-full border border-transparent px-1.5 py-1 hover:bg-muted hover:border-border transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-1"
        data-testid="user-menu-trigger"
      >
        <Avatar className="w-8 h-8">
          {user.avatar_url ? <AvatarImage src={user.avatar_url} alt={displayName} /> : null}
          <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
            {initials}
          </AvatarFallback>
        </Avatar>
        <span className="hidden lg:inline text-sm text-foreground max-w-[140px] truncate">
          {displayName}
        </span>
        <ChevronDown className="w-4 h-4 text-muted-foreground" />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-60 z-[10001]"
        data-testid="user-menu-content"
      >
        <DropdownMenuLabel className="pb-1">
          <div className="font-medium truncate">{displayName}</div>
          <div className="text-xs text-muted-foreground truncate">{user.email}</div>
          <div className="text-[10px] text-muted-foreground uppercase mt-1">{user.role}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem
          onSelect={() => navigate('/profile')}
          className="cursor-pointer"
          data-testid="user-menu-profile"
        >
          <UserIcon className="w-4 h-4 mr-2" />
          View profile
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate('/settings')}
          className="cursor-pointer"
          data-testid="user-menu-settings"
        >
          <SettingsIcon className="w-4 h-4 mr-2" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate('/settings/security')}
          className="cursor-pointer"
          data-testid="user-menu-security"
        >
          <Smartphone className="w-4 h-4 mr-2" />
          Security &amp; devices
        </DropdownMenuItem>
        {isAdmin ? (
          <DropdownMenuItem
            onSelect={() => navigate('/settings?tab=system-health')}
            className="cursor-pointer"
            data-testid="user-menu-system-health"
          >
            <ShieldCheck className="w-4 h-4 mr-2" />
            System health
          </DropdownMenuItem>
        ) : null}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onSelect={handleLogout}
          className="cursor-pointer text-rose-600 focus:text-rose-700 focus:bg-rose-50"
          data-testid="user-menu-logout"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Log out
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); setConfirmOpen(true); }}
          className="cursor-pointer text-rose-600 focus:text-rose-700 focus:bg-rose-50"
          data-testid="user-menu-logout-everywhere"
        >
          <ShieldOff className="w-4 h-4 mr-2" />
          Sign out everywhere
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <AlertDialogContent className="z-[10002]" data-testid="logout-everywhere-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>Sign out from all devices?</AlertDialogTitle>
          <AlertDialogDescription>
            This will revoke every active session for your account — including phones, tablets, browsers, and shared workstations. You&rsquo;ll need to log in again on each device. Use this if you suspect your account has been accessed by someone else.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="logout-everywhere-cancel" disabled={revoking}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleSignOutEverywhere}
            disabled={revoking}
            data-testid="logout-everywhere-confirm-btn"
            className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-500"
          >
            {revoking ? 'Revoking…' : 'Sign out everywhere'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
